import React, { useState, useRef, useCallback, useMemo } from 'react';
import SalesFilters from './SalesFilters';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Button, ButtonGroupContext } from '@mui/material';
import { DownloadIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@material-tailwind/react';

function SalesPerUnit({ data }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedConfig, setSelectedConfig] = useState('all');
    const [selectedBuilding, setSelectedBuilding] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [timePeriod, setTimePeriod] = useState('all');
    const gridRef = useRef(null);
    const [projectFilter, setProjectFilter] = useState('all');
    const customerSalesData = data || [];
    const navigate = useNavigate();

    const [isReadOnly, setIsReadOnly] = useState(false); // Example isReadOnly state.  Replace with your logic.

    const getBuildingNo = (item) => {
        return item?.propertyData?.buildingNo || 'N/A';
    };

    const getSizeBHK = (item) => {
        return item?.propertyConfigData?.configName || 'N/A';
    };

    const getUnitNo = (item) => {
        return item?.propertyData?.propertyNo || 'N/A';
    };

    const getCustomerName = (item) => {
        return `${item?.customerSale?.user?.firstName} ${item?.customerSale?.user?.lastName}` || 'N/A';
    };

    const soldUnits = useMemo(() => {
        return customerSalesData.filter(item => item?.customerSale?.propertyData?.status === 'Sold').map(item => ({
            unitId: item?.customerSale?.customerSalesId,
            project: item?.customerSale?.propertyData?.projectPhase?.project?.projectName || 'Unknown Project',
            configuration: getSizeBHK(item),
            buildingNo: getBuildingNo(item),
            unitNo: getUnitNo(item),
            source: item?.customerSale?.source || 'N/A',
            basePrice: item?.customerSale?.basePrice || 0,
            finalSalePrice: item?.customerSale?.basePrice || 0,
            dateSold: item?.customerSale?.saleDate || 'N/A',
            customerName: getCustomerName(item),
            customerSales: item.customerSale,
        }));
    }, [customerSalesData]);

    const uniqueConfigs = useMemo(() => ['all', ...new Set(soldUnits.map(item => item.configuration))], [soldUnits]);
    const uniqueBuildings = useMemo(() => ['all', ...new Set(soldUnits.map(item => item.buildingNo))], [soldUnits]);

    const uniqueProjects = useMemo(() => {
        return [...new Set(soldUnits.map(item => item.project))];
    }, [soldUnits]);

    const filteredData = useMemo(() => {
        return soldUnits.filter(item => {
            const searchMatch = item.project?.toLowerCase().includes(searchTerm.toLowerCase());
            const configMatch = selectedConfig === 'all' || item.configuration === selectedConfig;
            const buildingMatch = selectedBuilding === 'all' || item.buildingNo === selectedBuilding;
            const projectMatch = projectFilter === 'all' || item.project === projectFilter;

            let dateMatch = true;
            if (startDate && endDate) {
                if (item.dateSold && typeof item.dateSold === 'string') {
                    const saleDateObj = new Date(item.dateSold);
                    const startDateObj = new Date(startDate);
                    const endDateObj = new Date(endDate);
                    dateMatch = saleDateObj >= startDateObj && saleDateObj <= endDateObj;
                } else {
                    dateMatch = false;
                }
            }

            return searchMatch && configMatch && buildingMatch && projectMatch && dateMatch;
        });
    }, [soldUnits, searchTerm, selectedConfig, selectedBuilding, startDate, endDate, projectFilter]);

    const handleView = (data) => {
        navigate(`/properties/sold/${data.customerSales.customerSalesId}?projectId=${data.customerSales.propertyData.projectPhase.project.projectId}`);
    };

    const unitNoCellRenderer = useCallback((params) => {
        return (
            <Tooltip placement="right" contentClassName="bg-slate-800 text-white font-normal text-sm">
                <Tooltip.Trigger>
                    <button
                       className={`text-blue-500 hover:text-blue-700 cursor-pointer pl-4 underline`}
                        onClick={() => handleView(params.data)}
                       
                    
                    >
                        {params.value}
                    </button>
                </Tooltip.Trigger>
                 <Tooltip.Content className="bg-slate-800 text-white font-normal text-sm">
                             View property details
                              <Tooltip.Arrow />
                            </Tooltip.Content>
            </Tooltip>
        );
    }, [navigate]);

    const columnDefs = useMemo(() => [
        {
            field: 'unitNo',
            headerName: 'Unit No',
            cellRenderer: unitNoCellRenderer,
        },
        { field: 'customerName', headerName: 'Customer Name' },
        { field: 'project', headerName: 'Project Name' },
        { field: 'configuration', headerName: 'Configuration' },
        { field: 'buildingNo', headerName: 'Building No' },
        { field: 'source', headerName: 'Source' },
        {
            field: 'basePrice', headerName: 'Base Price (₹)', valueFormatter: params => {
                if (params.value) {
                    return '₹' + params.value.toLocaleString();
                } else {
                    return '₹0';
                }
            }
        },
        { field: 'dateSold', headerName: 'Date Sold' }
    ], [unitNoCellRenderer]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
    }), []);

    const onGridReady = useCallback((params) => {
        params.api.sizeColumnsToFit();
    }, []);

    const exportToCsv = useCallback(() => {
        const csvExportParams = {
            fileName: 'sales_per_unit.csv',
        };
        gridRef.current.api.exportDataAsCsv(csvExportParams);
    }, []);

    return (
        <div id="salesPerUnit" className="p-2">
            <div className="flex flex-wrap items-center gap-2">
                <SalesFilters
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    selectedConfig={selectedConfig}
                    setSelectedConfig={setSelectedConfig}
                    selectedBuilding={selectedBuilding}
                    setSelectedBuilding={setSelectedBuilding}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    timePeriod={timePeriod}
                    setTimePeriod={setTimePeriod}
                    uniqueConfigs={uniqueConfigs}
                    uniqueBuildings={uniqueBuildings}
                    showSearchTerm={true}
                    showConfigFilter={true}
                />
                <select
                    id="incomeProjectFilter"
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    className="p-2 border rounded w-40 h-1/2 md:w-auto font-normal text-sm"
                >
                    <option key="all" value="all">All Projects</option>
                    {uniqueProjects.map((project) => (
                        <option key={project} value={project}>
                            {project}
                        </option>
                    ))}
                </select>
                <button
                            className="flex items-center gap-2 font-medium text-sm text-white bg-green-600 hover:bg-green-700 p-2 rounded shadow-md"
                            onClick={exportToCsv}

                        >
                            <DownloadIcon size={18} /> Export
                        </button>

               
            </div>
            <div className="ag-theme-quartz font-normal mt-4" style={{ height: 500, width: '100%' }}>
                <AgGridReact
                    ref={gridRef}
                    rowData={filteredData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowSelection="single"
                    animateRows={true}
                    onGridReady={onGridReady}
                    onFirstDataRendered={params => {
                        params.api.sizeColumnsToFit();
                    }}
                />
            </div>
        </div >
    );
}

export default SalesPerUnit;